import { ref } from 'vue';
import type { Ref } from 'vue';
import api from '@/api';
import { MAX_SITE_EXCLUSIONS } from '@/constants';
import { isAxiosError } from 'axios';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import { AdpDocument, isArticle, PutExcludeSiteAndArticleError } from '@/types';
import { useUserSettingsNews } from '@/utils/swr';
import { useEmitter } from '@/utils/vue';

// パーソナルニュースの「このメディアを除外」アクション。
// `isEnabled` が true のとき（パーソナルニュースのメニューを表示するときだけ）
// ユーザー設定を取得するため、それ以外のカードでは余計な fetch を発生させない。
export const usePersonalFeedSiteExclusion = (isEnabled: Ref<boolean>) => {
  const { createSnackbar } = useSnackbar();
  const { data: userSettings, mutate } = useUserSettingsNews(
    () => isEnabled.value,
  );
  const emitter = useEmitter();
  const isExcludingMedia = ref(false);

  const excludeMediaFromPersonalNews = async (doc: AdpDocument) => {
    if (!isArticle(doc) || isExcludingMedia.value) return;

    // INFO: themeから継承した古い仕様はチケットで改善される：https://anews.atlassian.net/browse/ANDEV-8765
    const excludedSiteCount =
      userSettings.value?.personal_feeds_excluded_site_ids?.length ?? 0;
    if (excludedSiteCount >= MAX_SITE_EXCLUSIONS) {
      createSnackbar({
        message: `上限（${MAX_SITE_EXCLUSIONS}件）に達しています。登録したメディアは設定から変更できます。`,
        type: 'error',
      });
      return;
    }

    isExcludingMedia.value = true;
    try {
      await api.excludePersonalFeedsSiteAndArticle(doc.id, doc.lang);

      createSnackbar({
        message: 'メディアを除外しました',
        description: '7:00または13:00の配信から反映されます。',
        type: 'success',
      });
      await mutate();
      emitter.emit('personal-feed-article-removed', doc);
    } catch (err) {
      if (
        isAxiosError<PutExcludeSiteAndArticleError>(err) &&
        err.response?.data.error === 'site limit exceed'
      ) {
        createSnackbar({
          message: `上限（${MAX_SITE_EXCLUSIONS}件）に達しています`,
          description: '登録したメディアは設定からいつでも変更できます。',
          type: 'error',
        });
      } else if (
        isAxiosError<PutExcludeSiteAndArticleError>(err) &&
        err.response?.data.error === 'site already excluded'
      ) {
        createSnackbar({
          message: '既に登録しているメディアです',
          type: 'error',
        });
      } else {
        createSnackbar({
          message: 'メディアを除外できませんでした',
          type: 'error',
        });
        throw err;
      }
    } finally {
      isExcludingMedia.value = false;
    }
  };

  return { excludeMediaFromPersonalNews };
};
